import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import type { QueueVoicemailDefaultInfo, QueueVoicemailDefaultParams } from '~/server_shared/openapi/data-contracts';
import { isHiddenRole } from '~/Utils';

import CardDialog from '../../../../CardDialog';
// TextFieldFormik is a JSX module without type declarations.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { TextFieldFormik as TextFieldFormikUntyped } from '../../../../TextFieldFormik';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import VoiceMessageConfigurationDialog from '../../PhoneConfigurationTab/VoiceMessageConfigurationDialog/VoiceMessageConfigurationDialog';

import { useStyles } from '../../../../../assets/styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TextFieldFormik = TextFieldFormikUntyped as any;

// Single-brace token format — matches TextAreaWithTokens serialization
// (see frontend/src/components/core/Formik/TextAreaWithTokens.jsx) and the
// backend substitution in communications_manager._render_queue_voicemail_twiml.
const ALLOWED_TOKENS = { queue_name: { desc: 'Queue Name' } };

const FORM_IDS = {
  GREETING_TYPE: 'voicemail_message_configuration.configuration_type',
  GREETING_TEXT: 'voicemail_message_configuration.message_text',
  VOICE_FILE_OBJECT: 'voicemail_message_configuration.file',
  STORED_FILE_ID: 'voicemail_message_configuration.stored_file_id',
  FALLBACK_USER: 'fallback_notification_user_id',
  TASK_TIMEOUT: 'task_timeout_seconds',
};

const TASK_TIMEOUT_MIN_SECONDS = 30;
const TASK_TIMEOUT_MAX_SECONDS = 1200;
const TASK_TIMEOUT_DEFAULT_SECONDS = 60;

interface UserOption {
  id: string;
  username: string;
  role: { role_type: string };
}

interface EditQueueVoicemailDefaultsProps {
  configuration?: QueueVoicemailDefaultInfo | null;
  onSubmit: (values: QueueVoicemailDefaultParams) => Promise<void>;
  onCancel: () => void;
}

const EditQueueVoicemailDefaults: React.FC<EditQueueVoicemailDefaultsProps> = ({
  configuration,
  onSubmit,
  onCancel,
}) => {
  const { classes } = useStyles();
  // useSysconfig is JSX-typed; cast for safety while accessing `users`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { users } = useSysconfig() as { users: UserOption[] };
  const [allowSavingWithoutNewFile, setAllowSavingWithoutNewFile] = useState<boolean>(true);

  const existingGreeting = configuration?.voicemail_message_configuration ?? null;
  const initialType = existingGreeting?.configuration_type || 'text_message';

  return (
    <Formik
      initialValues={{
        voicemail_message_configuration: {
          configuration_type: initialType,
          message_text: existingGreeting?.message_text || '',
          stored_file_id: existingGreeting?.stored_file_id || null,
          file: null,
        },
        fallback_notification_user_id: configuration?.fallback_notification_user_id || '',
        task_timeout_seconds: configuration?.task_timeout_seconds ?? TASK_TIMEOUT_DEFAULT_SECONDS,
      }}
      validationSchema={Yup.object().shape({
        voicemail_message_configuration: Yup.object().shape({
          configuration_type: Yup.string().oneOf(['text_message', 'uploaded_text_message']).required(),
          message_text: Yup.string().when('configuration_type', {
            is: 'text_message',
            then: (schema) => schema.required('Greeting text is required'),
          }),
          stored_file_id: Yup.mixed().when('configuration_type', {
            is: 'uploaded_text_message',
            then: (schema) =>
              allowSavingWithoutNewFile ? schema.nullable() : schema.required('Voice file is required'),
          }),
        }),
        fallback_notification_user_id: Yup.string().required('Fallback notification user is required'),
        task_timeout_seconds: Yup.number()
          .required('Required')
          .integer()
          .min(TASK_TIMEOUT_MIN_SECONDS, `Minimum ${TASK_TIMEOUT_MIN_SECONDS}s`)
          .max(TASK_TIMEOUT_MAX_SECONDS, `Maximum ${TASK_TIMEOUT_MAX_SECONDS}s`),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit({
            voicemail_message_configuration: {
              configuration_type: values.voicemail_message_configuration.configuration_type,
              message_text:
                values.voicemail_message_configuration.configuration_type === 'text_message'
                  ? values.voicemail_message_configuration.message_text || null
                  : null,
              stored_file_id:
                values.voicemail_message_configuration.configuration_type === 'uploaded_text_message'
                  ? values.voicemail_message_configuration.stored_file_id || null
                  : null,
            },
            fallback_notification_user_id: values.fallback_notification_user_id || null,
            task_timeout_seconds: values.task_timeout_seconds,
          });
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit: formikHandleSubmit }) => {
        return (
          <CardDialog
            title="Queue Voicemail Defaults"
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
          >
            <div className="mb-8 mt-8">
              <TextFieldFormik id={FORM_IDS.FALLBACK_USER} select label="Fallback notification user" fullWidth>
                {users
                  .filter((u) => !isHiddenRole(u.role.role_type))
                  .map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.username}
                    </MenuItem>
                  ))}
              </TextFieldFormik>
            </div>

            <div className="mb-8">
              <TextFieldFormik
                id={FORM_IDS.TASK_TIMEOUT}
                type="number"
                label={`Wait before voicemail (seconds, ${TASK_TIMEOUT_MIN_SECONDS}–${TASK_TIMEOUT_MAX_SECONDS})`}
                fullWidth
              />
            </div>

            <VoiceMessageConfigurationDialog
              title="Default voicemail greeting"
              voiceMessageConfiguration={
                existingGreeting
                  ? {
                      configuration_type: existingGreeting.configuration_type || 'text_message',
                      message_text: existingGreeting.message_text || '',
                      stored_file_id: existingGreeting.stored_file_id || null,
                      permanent_audio_file_url: existingGreeting.permanent_audio_file_url || null,
                    }
                  : null
              }
              textAreaLabel="Voicemail greeting"
              textAreaId={FORM_IDS.GREETING_TEXT}
              voiceFileInputId={FORM_IDS.VOICE_FILE_OBJECT}
              storedFileIdPath={FORM_IDS.STORED_FILE_ID}
              radioButtonGroupId={FORM_IDS.GREETING_TYPE}
              allowedTokens={ALLOWED_TOKENS}
              onReplaceExistingRecording={() => {
                setAllowSavingWithoutNewFile(false);
              }}
            />

            <div className={classes.buttonsContainer}>
              <CancelButton disabled={isSubmitting} onClick={onCancel} />
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault();
                  formikHandleSubmit();
                }}
              >
                Save
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default EditQueueVoicemailDefaults;
